import { needsCarryoverDecision, operationalShiftAt } from "../../../../shared/production/plan-window";
import type { ProductionReferenceData, ProductionRun } from "../models";

export const plannedHoursForRun = (
  reference: ProductionReferenceData | null,
  run: ProductionRun | null,
) => {
  if (run?.planTotalHours && run.planTotalHours > 0) return run.planTotalHours;
  const scheduledHours = reference?.scheduleItems.find(
    (item) =>
      run &&
      item.type === "WORK_ORDER" &&
      item.date === run.shiftDate &&
      item.shiftCode === run.shiftCode &&
      item.workOrderNo === run.workOrderNo &&
      (!item.productCode || item.productCode === run.productCode),
  )?.plannedHours;
  if (scheduledHours && scheduledHours > 0) return scheduledHours;
  if (!run?.plannedStartAt || !run.plannedEndAt) return null;
  const duration = new Date(run.plannedEndAt).getTime() - new Date(run.plannedStartAt).getTime();
  return Number.isFinite(duration) && duration > 0 ? duration / 3_600_000 : null;
};

const isCurrentOperationalShift = (run: ProductionRun, now = Date.now()) => {
  const current = operationalShiftAt(now);
  return run.shiftDate === current.date && run.shiftCode === current.shiftCode;
};

export type RunTimingKind =
  | "READY"
  | "SOON"
  | "DUE"
  | "OVERDUE"
  | "CARRYOVER"
  | "OVERRUN"
  | "ACTIVE"
  | "COMPLETED";

export function runTiming(
  run: ProductionRun,
  reference: ProductionReferenceData | null,
  now = Date.now(),
) {
  const schedule = reference?.scheduleItems.find(
    (item) =>
      item.type === "WORK_ORDER" &&
      item.date === run.shiftDate &&
      item.shiftCode === run.shiftCode &&
      item.workOrderNo === run.workOrderNo &&
      (!item.lineId || item.lineId === run.lineId),
  );
  const plannedStart = run.plannedStartAt
    ? new Date(run.plannedStartAt).getTime()
    : schedule
      ? new Date(`${schedule.date}T${schedule.startTime}:00`).getTime()
      : Number.NaN;
  let plannedEnd = run.plannedEndAt
    ? new Date(run.plannedEndAt).getTime()
    : schedule
      ? new Date(`${schedule.date}T${schedule.endTime}:00`).getTime()
      : Number.NaN;
  if (!Number.isNaN(plannedStart) && !Number.isNaN(plannedEnd) && plannedEnd <= plannedStart) {
    plannedEnd += 24 * 60 * 60 * 1_000;
  }
  const plannedTime = Number.isNaN(plannedStart)
    ? null
    : new Intl.DateTimeFormat("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(plannedStart);
  const displayedPlanTime = run.planFlowKey && run.plannedStartAt
    ? new Intl.DateTimeFormat("tr-TR",{timeZone:"Europe/Istanbul",day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}).format(new Date(run.plannedStartAt))
    : plannedTime;
  const startDelayMinutes =
    !Number.isNaN(plannedStart) && run.startedAt
      ? Math.max(0, Math.floor((new Date(run.startedAt).getTime() - plannedStart) / 60_000))
      : null;
  if (run.status === "COMPLETED") {
    return { kind: "COMPLETED" as RunTimingKind, label: "Tamamlandı", plannedTime, startDelayMinutes };
  }
  if (run.status === "ACTIVE") {
    const plannedHours = plannedHoursForRun(reference, run);
    const activePlanEnd = !Number.isNaN(plannedStart) && plannedHours
      ? plannedStart + plannedHours * 3_600_000
      : plannedEnd;
    if (!Number.isNaN(activePlanEnd) && now > activePlanEnd) {
      const overrunMinutes = Math.max(1, Math.floor((now - activePlanEnd) / 60_000));
      return { kind: "OVERRUN" as RunTimingKind, label: `Plan süresi ${overrunMinutes} dk aşıldı`, plannedTime, overrunMinutes, startDelayMinutes };
    }
    return { kind: "ACTIVE" as RunTimingKind, label: run.planItemKind === "ACTIVITY" ? "Plan etkinliği devam ediyor" : "Üretim devam ediyor", plannedTime, startDelayMinutes };
  }
  if (Number.isNaN(plannedStart)) {
    return { kind: "READY" as RunTimingKind, label: "Başlamaya hazır", plannedTime, startDelayMinutes };
  }
  const differenceMinutes = Math.floor((now - plannedStart) / 60_000);
  if (needsCarryoverDecision(run, now)) {
    return { kind: "CARRYOVER" as RunTimingKind, label: "Amir kararı bekleniyor", plannedTime, delayMinutes: differenceMinutes, startDelayMinutes };
  }
  if (!Number.isNaN(plannedEnd) && now > plannedEnd) {
    if (run.planFlowKey || isCurrentOperationalShift(run, now)) {
      return { kind: "OVERDUE" as RunTimingKind, label: `${differenceMinutes} dk gecikti`, plannedTime, delayMinutes: differenceMinutes, startDelayMinutes };
    }
    return { kind: "CARRYOVER" as RunTimingKind, label: "Önceki vardiyadan kaldı", plannedTime, delayMinutes: differenceMinutes, startDelayMinutes };
  }
  if (differenceMinutes > 15) {
    return { kind: "OVERDUE" as RunTimingKind, label: `${differenceMinutes} dk gecikti`, plannedTime, delayMinutes: differenceMinutes, startDelayMinutes };
  }
  if (differenceMinutes >= 0) {
    return { kind: "DUE" as RunTimingKind, label: "Başlama zamanı geldi", plannedTime, delayMinutes: differenceMinutes, startDelayMinutes };
  }
  if (differenceMinutes >= -10) {
    return { kind: "SOON" as RunTimingKind, label: `${Math.abs(differenceMinutes)} dk sonra başlayacak`, plannedTime, startDelayMinutes };
  }
  return { kind: "READY" as RunTimingKind, label: "Gelecek plan · başlangıç bekleniyor", plannedTime: displayedPlanTime, startDelayMinutes };
}
